/**
 * Карточки тарифов: на телефоне — горизонтальная карусель со «свайпом»
 * (CSS scroll-snap: прокрутка нативная, инерция и жесты — от браузера), на
 * широком экране (`lg`) — обычная сетка рядом. Соседняя карточка
 * выглядывает из-за края — видно, что можно листать; точки внизу
 * показывают, какая открыта, и листают по касанию.
 */

import { Children, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

interface PlanCarouselProps {
  readonly children: ReactNode;
  /** Подписи карточек — для точек и экранного диктора. */
  readonly labels: readonly string[];
  readonly label: string;
  /** С какой карточки открыть карусель (обычно — рекомендуемый тариф). */
  readonly initial?: number;
  /** Колонки сетки на широком экране. */
  readonly desktopColumns?: 2 | 3;
  /**
   * Отступ карусели «под край» родителя: карусель выходит на поля
   * контейнера, чтобы карточки уезжали за край экрана, а не обрезались.
   */
  readonly bleed?: 'page' | 'modal';
}

const BLEED = {
  // Поля `main` — px-4 на телефоне.
  page: '-mx-4 px-4 scroll-px-4',
  // Поля модалки — p-5 на телефоне, p-6 с `sm`.
  modal: '-mx-5 px-5 scroll-px-5 sm:-mx-6 sm:px-6 sm:scroll-px-6',
} as const;

const DESKTOP_COLUMNS = { 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3' } as const;

export function PlanCarousel({ children, labels, label, initial = 0, desktopColumns = 3, bleed = 'page' }: PlanCarouselProps) {
  const track = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(initial);
  const slides = Children.toArray(children);

  const slideAt = (index: number): HTMLElement | null => (track.current?.children[index] as HTMLElement | undefined) ?? null;

  /** Прокрутить к карточке по центру; на сетке (`lg`) прокрутки нет — ничего не делает. */
  const scrollTo = (index: number, behavior: 'auto' | 'smooth') => {
    const container = track.current;
    const slide = slideAt(index);
    if (container === null || slide === null) return;
    container.scrollTo({ left: slide.offsetLeft - (container.clientWidth - slide.clientWidth) / 2, behavior });
  };

  // Открываемся сразу на рекомендуемой карточке — без видимой прокрутки.
  useLayoutEffect(() => {
    scrollTo(initial, 'auto');
    setActive(initial);
    // Только при первом показе и смене рекомендации.
  }, [initial]);

  // Активная точка — карточка, чей центр ближе к центру карусели.
  useEffect(() => {
    const container = track.current;
    if (container === null) return undefined;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const center = container.scrollLeft + container.clientWidth / 2;
        let best = 0;
        let bestDistance = Number.POSITIVE_INFINITY;
        Array.from(container.children).forEach((child, index) => {
          const element = child as HTMLElement;
          const distance = Math.abs(element.offsetLeft + element.clientWidth / 2 - center);
          if (distance < bestDistance) {
            best = index;
            bestDistance = distance;
          }
        });
        setActive(best);
      });
    };
    container.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      container.removeEventListener('scroll', onScroll);
    };
  }, []);

  return (
    <div className="flex flex-col gap-3">
      <div
        ref={track}
        role="region"
        aria-roledescription="carousel"
        aria-label={label}
        data-testid="plan-carousel"
        className={[
          // Телефон: лента со снапом по центру, без полосы прокрутки.
          'relative flex snap-x snap-mandatory items-stretch gap-3 overflow-x-auto overscroll-x-contain pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          BLEED[bleed],
          // Широкий экран: обычная сетка рядом.
          'lg:mx-0 lg:grid lg:snap-none lg:gap-4 lg:overflow-visible lg:px-0 lg:pb-0',
          DESKTOP_COLUMNS[desktopColumns],
        ].join(' ')}
      >
        {slides.map((slide, index) => (
          <div
            key={index}
            role="group"
            aria-roledescription="slide"
            aria-label={labels[index]}
            className="flex w-[84%] max-w-sm shrink-0 snap-center snap-always flex-col lg:w-auto lg:max-w-none"
          >
            {slide}
          </div>
        ))}
      </div>
      {slides.length > 1 && (
        <div className="flex justify-center gap-2 lg:hidden">
          {slides.map((_slide, index) => (
            <button
              key={index}
              type="button"
              onClick={() => scrollTo(index, 'smooth')}
              aria-label={labels[index]}
              aria-current={active === index}
              className="grid h-6 w-6 place-items-center"
            >
              <span className={['block h-2 rounded-full transition-all', active === index ? 'w-6 bg-accent' : 'w-2 bg-heading/20'].join(' ')} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
